import React, {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useState,
} from "react";
import {
  sanitiseString,
  concatStringFromClassesArrayList,
} from "../../Utils";

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
type TabAttributes = { [key: string]: string };

export interface Tab {
  id?: string;
  title: string;
  showTitle?: boolean;
  content: React.ReactNode;
  attributes?: TabAttributes;
}

export interface Props {
  title: string;
  headingLevel?: HeadingLevel;
  titleClasses?: string;
  tabs: Tab[];
  dataTestId?: string;
}

const TAB_BREAKPOINT: number = 450;

const getPanelId = (tab: Tab): string => sanitiseString(tab.id || tab.title);

const getTabId = (tab: Tab): string => `tab-${getPanelId(tab)}`;

const getTitleClassName = (
  titleClasses: string | undefined,
  isTabMode: boolean
): string =>
  concatStringFromClassesArrayList({
    defaultClass: "ons-tabs__title ons-u-fs-r--b ons-u-mt-no",
    optionalClasses: [
      isTabMode ? "ons-u-vh" : null,
      titleClasses ? titleClasses : null,
    ],
  });

const getListClassName = (isTabMode: boolean): string =>
  concatStringFromClassesArrayList({
    defaultClass: "ons-tabs__list",
    optionalClasses: [isTabMode ? "ons-tabs__list--row" : null],
  });

const getListItemClassName = (isTabMode: boolean): string =>
  concatStringFromClassesArrayList({
    defaultClass: "ons-tab__list-item",
    optionalClasses: [isTabMode ? "ons-tab__list-item--row" : null],
  });

const getTabClassName = (isActive: boolean, isTabMode: boolean): string =>
  concatStringFromClassesArrayList({
    defaultClass: "ons-tab",
    optionalClasses: [
      isTabMode ? "ons-tab--row" : null,
      isTabMode && isActive ? "ons-tab--selected" : null,
    ],
  });

const getPanelClassName = (isActive: boolean, isTabMode: boolean): string =>
  concatStringFromClassesArrayList({
    defaultClass: "ons-tabs__panel",
    optionalClasses: [isTabMode && !isActive ? "ons-tabs__panel--hidden" : null],
  });

export const Tabs = (props: Props) => {
  const sanitisedDataTestId: string = sanitiseString(props.dataTestId);
  const headingLevel: HeadingLevel = props.headingLevel ?? 2;
  const titleTag: string = `h${headingLevel}`;
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isTabMode, setIsTabMode] = useState<boolean>(true);

  const updateTabMode = () => {
    setIsTabMode(window.innerWidth >= TAB_BREAKPOINT);
  };

  useEffect(() => {
    window.addEventListener("resize", updateTabMode);
    updateTabMode();

    return () => {
      window.removeEventListener("resize", updateTabMode);
    };
  }, []);

  const handleTabClick = (
    event: MouseEvent<HTMLAnchorElement>,
    index: number
  ) => {
    if (!isTabMode) {
      return;
    }

    event.preventDefault();
    setActiveIndex(index);
  };

  const handleTabKeyDown = (
    event: KeyboardEvent<HTMLAnchorElement>,
    index: number
  ) => {
    if (!isTabMode) {
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      document.getElementById(getTabId(props.tabs[index - 1]))?.focus();
    } else if (event.key === "ArrowRight" && index < props.tabs.length - 1) {
      event.preventDefault();
      document.getElementById(getTabId(props.tabs[index + 1]))?.focus();
    } else if (event.key === " ") {
      event.preventDefault();
      setActiveIndex(index);
    }
  };

  const titleClassName: string = getTitleClassName(
    props.titleClasses,
    isTabMode
  );

  return (
    <section className="ons-tabs" data-testid={sanitisedDataTestId}>
      {React.createElement(
        titleTag,
        {
          className: titleClassName,
          "data-testid": `${sanitisedDataTestId}-title`,
        },
        props.title
      )}
      <div className={isTabMode ? "ons-tabs__container" : undefined}>
        <ul
          className={getListClassName(isTabMode)}
          {...(isTabMode ? { role: "tablist" } : {})}
          data-testid={`${sanitisedDataTestId}-list`}
        >
          {props.tabs.map((tab: Tab, index: number) => {
            const panelId: string = getPanelId(tab);
            const tabId: string = getTabId(tab);
            const isActive: boolean = index === activeIndex;
            const tabClassName: string = getTabClassName(isActive, isTabMode);

            return (
              <li
                key={panelId}
                className={getListItemClassName(isTabMode)}
                {...(isTabMode ? { role: "presentation" } : {})}
              >
                <a
                  href={`#${panelId}`}
                  className={tabClassName}
                  data-testid={`${sanitisedDataTestId}-tab-${panelId}`}
                  onClick={(event) => handleTabClick(event, index)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                  {...(isTabMode
                    ? {
                        id: tabId,
                        role: "tab",
                        "aria-controls": panelId,
                        "aria-selected": isActive,
                        tabIndex: isActive ? 0 : -1,
                      }
                    : {})}
                  {...(tab.attributes ?? {})}
                >
                  {tab.title}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
      {props.tabs.map((tab: Tab, index: number) => {
        const panelId: string = getPanelId(tab);
        const tabId: string = getTabId(tab);
        const isActive: boolean = index === activeIndex;
        const panelClassName: string = getPanelClassName(isActive, isTabMode);

        return (
          <section
            key={panelId}
            id={panelId}
            className={panelClassName}
            data-testid={`${sanitisedDataTestId}-panel-${panelId}`}
            {...(isTabMode
              ? {
                  role: "tabpanel",
                  "aria-labelledby": tabId,
                  tabIndex: 0,
                }
              : {})}
          >
            {tab.showTitle && (
              <h2
                id={`${sanitiseString(tab.title)}-content-title`}
                className={isTabMode ? "ons-u-vh" : undefined}
              >
                {tab.title}
              </h2>
            )}
            {tab.content}
          </section>
        );
      })}
    </section>
  );
};
